import { Component, OnInit, inject } from '@angular/core';
import { Router, RouterLink } from '@angular/router';
import { GameEngineService, GameService, InternationalizationService, 
  SessionService, TransitionService, User } from '@app/core/services';
import { Clipboard } from '@angular/cdk/clipboard';
import { Subscription } from 'rxjs';
import { FormsModule } from '@angular/forms';
import { AddJeopardyGameInput, JeopardyGame, JeopardyRoom } from '@app/shared/graphql/types';

@Component({
    selector: 'jeopardy-create-room',
    templateUrl: './create-room.component.html',
    styleUrls: ['./create-room.component.scss'],
    imports: [FormsModule, RouterLink]
})
export class CreateRoomComponent implements OnInit {
  public playersNumber: number;
  public currentUser: User;
  public roomName: string;
  public privateRoom: boolean;
  public currentDevice: string[];
  public rooms: JeopardyRoom[];
  public selectedRoom: JeopardyRoom;
  public suggestions: JeopardyRoom[];
  public accessCode: string;
  public showCode: boolean;
  private game: JeopardyGame;
  private subscriptions: Subscription;
  public roomIsCreated: boolean;

  public i18n = inject(InternationalizationService);
  public router = inject(Router);
  public gameService = inject(GameService);
  public sessionService = inject(SessionService);
  public clipboard = inject(Clipboard);
  public transitionService = inject(TransitionService);
  public gameEngineService = inject(GameEngineService);

  ngOnInit(): void {
    this.roomIsCreated = false;
    this.subscriptions = new Subscription();
    const gameId = this.gameService.getGameId();

    if( gameId && gameId !== 'null' ) {
      const getGameDetailsSub = this.gameService.getGameDetails(+gameId)
      .subscribe((room: JeopardyGame) => {
        if(room && room.active) {
          this.navigateTo(this.i18n.str('challengeu.msg_go_to_lobby', 'Going to lobby...')(), ['/multiplayer/lobby', gameId]);
          return;
        }
      });

      this.subscriptions.add(getGameDetailsSub);
    }
    
    this.accessCode = '';
    this.currentDevice = this.gameService.getDeviceDetails();
    this.currentUser =  this.sessionService.getUser();
    this.playersNumber = 2;
    this.roomName = '';
    this.privateRoom = false;
    this.selectedRoom = null;
    this.suggestions = [];
    this.showCode = false;

    const roomNamesSub$ = this.gameService.getRoomNames().subscribe(rooms => {
      this.rooms = rooms || [];
    });

    this.subscriptions.add(roomNamesSub$);
  }

  public selectPlayersNumber(event: Event, no: number) {
    event.preventDefault();

    this.playersNumber = no;
  }

  public createRoom(event: Event) {
    event.preventDefault();

    this.roomIsCreated = true;

    const gameInput: AddJeopardyGameInput = {
      multiplayer: true,
      roomName: this.selectedRoom.roomName,
      langue: this.currentUser.location.language.isoCode4Letters,
      players: this.playersNumber,
      private: this.privateRoom,
      advancedMode: true
    };

    const createGameSub = this.gameService.createGame(gameInput).subscribe((game) => {
      this.gameEngineService.init(game?.gameId).subscribe(room => {
        this.roomIsCreated = false;

        this.game = room;
        const host = this.gameEngineService.players.filter(player => player.host)[0];

        this.gameService.setGameId(this.game?.gameId);
        this.sessionService.updateUserGameId(this.game?.gameId);
       
        if(host) {
          const userUpdate = {
            ...host,
            name: this.currentUser.username,
            connected: host?.connected,
            language: this.currentUser.location.language?.isoCode2Letters,
            location: this.currentUser.location.country?.countryId,
            deviceDetected: this.currentDevice[0],
            deviceIdentifier: this.currentDevice[1],
            avatar: this.currentUser.avatar?.split('/')?.pop(),
            score: host?.score,
            host: true,
            jokerFiftyFifty: host.jokerFiftyFifty || 0,
            jokerSkip: host.jokerFiftyFifty || 0
          };

          const updateUserSub = this.gameService.updateUser(userUpdate).subscribe(_ => {
            const loadPlayersSub = this.gameEngineService.loadPlayers().subscribe(_ => {
              if(this.privateRoom) {
                this.accessCode = game.roomCode;
                this.showCode = true;
              } else {
                this.goToLobby(null);
              }
            });

            this.subscriptions.add(loadPlayersSub);
          });

          this.subscriptions.add(updateUserSub);
        }

        if(this.privateRoom) {
          const roundStartedSub =  this.gameService.getRoundStartedSubscription(room.gameId)
          .subscribe(_ => {
            this.navigateTo(this.i18n.str('challengeu.msg_loading_question', 'Loading question...')(), ['question']);
          });

          this.subscriptions.add(roundStartedSub); 

          const userJoinedSub =  this.gameService.getUserConnectedSubscription(room.gameId)
            .subscribe(_ => {
              const loadPlayersSub = this.gameEngineService.loadPlayers().subscribe(_ => {
                const connected = this.gameEngineService.players.filter(player => player.connected);
                if(connected.length === this.game.players) {
                  this.navigateTo(this.i18n.str('challengeu.msg_starting_game', 'Starting game...')(), ['select-topic']);
                }
              });
              this.subscriptions.add(loadPlayersSub);
            });
          this.subscriptions.add(userJoinedSub);
        }
      },
      err => {
        console.log(err);
      });
    });

    this.subscriptions.add(createGameSub);
  }

  public filterRooms() {
    this.selectedRoom = null;

    if(this.roomName.length > 1) {
      this.suggestions = this.rooms.filter(room => room.roomName.toLowerCase().includes(this.roomName.toLowerCase()));
    } else {
      this.suggestions = [];
    }
  }

  public selectRoom(ev: Event, room: JeopardyRoom) {
    ev.preventDefault();
    this.selectedRoom = room;
  }

  public copyCode(ev: Event) {
    ev.preventDefault();

    this.clipboard.copy(this.accessCode);
  }

  public goToLobby(ev: Event){
    if(ev) {
      ev.preventDefault();
    }

    this.navigateTo(this.i18n.str('challengeu.msg_go_to_lobby', 'Going to lobby...')(), ['/multiplayer/lobby', this.game?.gameId?.toString() ?? '']);
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
  }

  private navigateTo(message: string, urls: string[]) {
    this.transitionService.showGameLoader(message);

    setTimeout(() => {
      this.router.navigate(urls);

      setTimeout(() => {
        this.transitionService.hideGameLoader();
      }, 500);
    }, 500);
  }
}
